import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  almanacExtract,
  applyDecIncrement,
  applyGhaIncrement,
  formatDeclination,
  formatDegMin,
  type AlmanacBody,
} from "@nav-games/astro";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface AlmanacDrillProps {
  onNavigate: (to: string) => void;
}

const MODULE = "astro";
const GAME = "almanac-increments";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

const BODY_ORDER: readonly AlmanacBody[] = [
  "sun",
  "aries",
  "venus",
  "mars",
  "jupiter",
  "saturn",
  "moon",
];

const BODY_LABEL: Record<AlmanacBody, string> = {
  sun: "Sun",
  aries: "Aries (stars)",
  venus: "Venus",
  mars: "Mars",
  jupiter: "Jupiter",
  saturn: "Saturn",
  moon: "Moon",
};

type Target = "gha" | "dec";

interface Card {
  body: AlmanacBody;
  hourStart: Date;
  m: number;
  s: number;
  hourlyGhaDeg: number;
  hourlyDecDeg: number;
  vPerHourMin: number;
  dPerHourMin: number;
  target: Target;
  /// Correct answer in formatted form (matches one of the choices).
  answer: string;
  choices: readonly string[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}
function randChoice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function buildCard(): Card {
  const body = randChoice(BODY_ORDER);
  // Pick a random date in the next year, random UT hour.
  const today = new Date();
  const days = randInt(0, 365);
  const date = new Date(today);
  date.setUTCDate(date.getUTCDate() + days);
  date.setUTCHours(randInt(6, 22), 0, 0, 0);
  const m = randInt(1, 59);
  const s = randInt(0, 59);

  const extract = almanacExtract(body, date);
  // For Aries: drill GHA only (no Dec column).
  const target: Target = body === "aries" ? "gha" : randChoice(["gha", "dec"]);

  let answer: string;
  let correctValueDeg: number;
  if (target === "gha") {
    correctValueDeg = applyGhaIncrement({
      body,
      hourlyGhaDeg: extract.ghaDeg,
      m,
      s,
      vPerHourMin: extract.vPerHourMin,
    });
    answer = formatDegMin(correctValueDeg, 1);
  } else {
    correctValueDeg = applyDecIncrement({
      hourlyDecDeg: extract.decDeg,
      m,
      s,
      dPerHourMin: extract.dPerHourMin,
    });
    answer = formatDeclination(correctValueDeg, 1);
  }

  const fmt = (deg: number): string =>
    target === "gha" ? formatDegMin(deg, 1) : formatDeclination(deg, 1);

  // Distractor candidates — the canonical student mistakes for each
  // body family.
  const candidates: number[] = [];
  if (target === "gha") {
    // forgot the increment
    candidates.push(extract.ghaDeg);
    // forgot the v-correction (relevant for Moon/planets)
    if (extract.vPerHourMin !== 0) {
      candidates.push(
        correctValueDeg - (extract.vPerHourMin * (m / 60 + s / 3600)) / 60,
      );
    }
    // applied nominal as 14.32°/hr instead of 15° (or vice versa) —
    // canonical Sun ↔ Moon mix-up
    const wrongNominal = body === "moon" ? 15.0 : 14.31667;
    candidates.push(
      extract.ghaDeg + (wrongNominal * (m / 60 + s / 3600)) +
        (extract.vPerHourMin * (m / 60 + s / 3600)) / 60,
    );
  } else {
    // forgot d-correction
    candidates.push(extract.decDeg);
    // wrong d sign
    candidates.push(
      correctValueDeg - (2 * extract.dPerHourMin * (m / 60 + s / 3600)) / 60,
    );
    // d applied as deg/hr instead of arcmin/hr — gives huge error
    candidates.push(
      extract.decDeg + extract.dPerHourMin * (m / 60 + s / 3600),
    );
  }

  const distractors: string[] = [];
  const seen = new Set<string>([answer]);
  for (const cand of candidates) {
    if (distractors.length >= CHOICES_PER_CARD - 1) break;
    const cstr = fmt(cand);
    if (!seen.has(cstr)) {
      seen.add(cstr);
      distractors.push(cstr);
    }
  }
  // Top up with random ± offsets in arc-min.
  let off = 4;
  while (distractors.length < CHOICES_PER_CARD - 1) {
    for (const cand of [correctValueDeg + off / 60, correctValueDeg - off / 60]) {
      if (distractors.length >= CHOICES_PER_CARD - 1) break;
      const cstr = fmt(cand);
      if (!seen.has(cstr)) {
        seen.add(cstr);
        distractors.push(cstr);
      }
    }
    off += 3;
  }

  const choices = [answer, ...distractors];
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = choices[i]!;
    choices[i] = choices[j]!;
    choices[j] = a;
  }

  return {
    body,
    hourStart: extract.hour,
    m,
    s,
    hourlyGhaDeg: extract.ghaDeg,
    hourlyDecDeg: extract.decDeg,
    vPerHourMin: extract.vPerHourMin,
    dPerHourMin: extract.dPerHourMin,
    target,
    answer,
    choices,
  };
}

export function AlmanacDrill({ onNavigate }: AlmanacDrillProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === card.answer;
      const attempt: Omit<Attempt, "module"> = {
        char: `${card.body}-${card.target}`,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/astro")}
      />
    );
  }

  const revealed = chosen !== null;
  const fracHour = card.m / 60 + card.s / 3600;
  const hourLabel = `${String(card.hourStart.getUTCHours()).padStart(2, "0")}h`;
  const minSec = `${String(card.m).padStart(2, "0")}m ${String(card.s).padStart(2, "0")}s`;
  const dateLabel = card.hourStart.toISOString().slice(0, 10);

  // Build the working strings on reveal.
  const hasV = card.vPerHourMin !== 0;
  const incArcmin =
    fracHour *
    (card.body === "aries" ? 15.04107 * 60 : card.body === "moon" ? 14.31667 * 60 : 15.0 * 60);
  const vArcmin = card.vPerHourMin * fracHour;
  const dArcmin = card.dPerHourMin * fracHour;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "astro" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Almanac increments — {BODY_LABEL[card.body]}</h3>
        <p style={{ marginBottom: 8 }}>
          Daily-page extract for <strong>{BODY_LABEL[card.body]}</strong> at{" "}
          <strong>{hourLabel} UT on {dateLabel}</strong>:
        </p>
        <div
          className="small"
          style={{
            padding: 10,
            marginBottom: 10,
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          <div>GHA ({hourLabel}) = <strong>{formatDegMin(card.hourlyGhaDeg, 1)}</strong></div>
          {card.body !== "aries" && (
            <div>
              Dec ({hourLabel}) = <strong>{formatDeclination(card.hourlyDecDeg, 1)}</strong>
              {"  "}<span className="muted">(d = {card.dPerHourMin >= 0 ? "+" : ""}{card.dPerHourMin.toFixed(1)}′/hr)</span>
            </div>
          )}
          {hasV && (
            <div>
              v = <strong>{card.vPerHourMin >= 0 ? "+" : ""}{card.vPerHourMin.toFixed(1)}′/hr</strong>
            </div>
          )}
        </div>
        <p style={{ marginBottom: 12 }}>
          Your sight is at <strong>{hourLabel} {minSec} UT</strong>. What is the{" "}
          <strong>{card.target === "gha" ? "GHA" : "Declination"}</strong> at the sight?
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c === card.answer;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 16,
                  padding: "12px 0",
                  fontWeight: 600,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.answer
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.answer
                ? `✓ Correct — ${card.target === "gha" ? "GHA" : "Dec"} = ${card.answer}.`
                : `✗ Not quite — ${card.target === "gha" ? "GHA" : "Dec"} = ${card.answer}.`}
            </p>
            <div
              className="small"
              style={{
                marginTop: 8,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                whiteSpace: "pre-wrap",
              }}
            >
              {card.target === "gha"
                ? `Increment = ${(card.body === "aries"
                    ? 15.04107
                    : card.body === "moon"
                      ? 14.31667
                      : 15.0).toFixed(5)}°/hr × ${fracHour.toFixed(5)}
            = ${incArcmin.toFixed(1)}′
${hasV
                    ? `v-correction = ${card.vPerHourMin.toFixed(1)}′/hr × ${fracHour.toFixed(5)} = ${vArcmin.toFixed(1)}′\n`
                    : ""}GHA(sight) = GHA(h:00) + increment${hasV ? " + v-corr" : ""}
           = ${formatDegMin(card.hourlyGhaDeg, 1)} + ${incArcmin.toFixed(1)}′${hasV ? ` + ${vArcmin.toFixed(1)}′` : ""}
           = ${card.answer}`
                : `d-correction = ${card.dPerHourMin >= 0 ? "+" : ""}${card.dPerHourMin.toFixed(1)}′/hr × ${fracHour.toFixed(5)}
            = ${dArcmin >= 0 ? "+" : ""}${dArcmin.toFixed(1)}′
Dec(sight) = Dec(h:00) + d-corr
          = ${formatDeclination(card.hourlyDecDeg, 1)} ${dArcmin >= 0 ? "+" : "−"} ${Math.abs(dArcmin).toFixed(1)}′
          = ${card.answer}`}
            </div>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/astro")}>
        ← Back to Astronavigation
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Astronavigation
        </button>
      </div>
    </>
  );
}
